import { api } from './client'
import type { UploadResult } from './types'

/** Whether the tenant has a live Google consent. */
export interface GoogleStatus {
  connected: boolean
  email: string | null
  connectedAt: string | null
}

/** One row of the recent-spreadsheets picker. */
export interface GoogleSheetFile {
  id: string
  name: string
  modifiedAt: string | null
}

/** A tab (worksheet) inside a spreadsheet. */
export interface GoogleTab {
  name: string
}

export interface GoogleSheetImportRequest {
  spreadsheetId: string
  tab?: string
}

/**
 * The Google Sheets source for step 2 of the import wizard.
 *
 * `importSheet` is the one call that matters downstream: it returns the same
 * `UploadResult` a local file upload does, so the mapping, validation and submit
 * steps are identical whichever source produced the data.
 */
export const googleSheetsApi = {
  /** Where the browser is sent to start Google consent. */
  connectUrl(): Promise<{ url: string }> {
    return api.get<{ url: string }>('/google/sheets/connect-url')
  },

  status(): Promise<GoogleStatus> {
    return api.get<GoogleStatus>('/google/sheets/status')
  },

  disconnect(): Promise<void> {
    return api.post<void>('/google/sheets/disconnect')
  },

  /** The user's most recently modified spreadsheets. */
  spreadsheets(): Promise<GoogleSheetFile[]> {
    return api.get<GoogleSheetFile[]>('/google/sheets/spreadsheets')
  },

  /** The tab names inside one spreadsheet, for the picker. */
  tabs(spreadsheetId: string): Promise<GoogleTab[]> {
    return api.get<GoogleTab[]>(`/google/sheets/spreadsheets/${encodeURIComponent(spreadsheetId)}/tabs`)
  },

  /** Ingests one tab and returns an upload equivalent to a local file upload. */
  importSheet(request: GoogleSheetImportRequest): Promise<UploadResult> {
    return api.post<UploadResult>('/google/sheets/import', request)
  },
}

/**
 * The spreadsheet id out of a Google Sheets URL, or the raw id if one was typed.
 *
 * Accepts the two shapes a user actually pastes — an edit URL
 * (`/spreadsheets/d/<id>/edit#gid=0`) and a published one — and returns null for
 * anything without an id in it, so the caller can say "that is not a sheet link"
 * rather than sending a bad id to the API.
 */
export function parseSpreadsheetId(input: string): string | null {
  const value = input.trim()
  if (!value) return null

  const match = value.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/)
  if (match) return match[1]

  // A bare id: Google's are long, url-safe base64-ish strings.
  if (/^[a-zA-Z0-9-_]{20,}$/.test(value)) return value

  return null
}
